/** Per-kind edit forms for proposal items — a reviewer edits the fields that
 * matter, with labels and help texts, never a raw JSON blob (that implied
 * knowing the wire format by heart). Edits merge over the stored payload so
 * cross-item refs ({"$item": id}) and machine fields survive untouched.
 * Kinds without a form (design documents, reserved kinds) fall back to a
 * clearly-labelled raw JSON editor.
 */
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as proposalsApi from '@/api/compositionProposals';
import type { ProposalItem } from '@/api/compositionProposals';
import { listEvents, type NotificationEvent } from '@/api/notifications';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';
import { TemplateCodeEditor } from '@/components/TemplateCodeEditor';

interface Props {
  proposalId: number;
  item: ProposalItem | null;
  onClose: () => void;
  onSaved: () => void;
}

interface AttributeRow {
  attribute: string;
  [key: string]: unknown;
}

const FORM_KINDS = new Set(['notification', 'delivery_method', 'block', 'tag_category', 'theme']);

export function ItemEditDialog({ proposalId, item, onClose, onSaved }: Props) {
  const snackbar = useSnackbar();
  const [saving, setSaving] = useState(false);

  // Shared field state, seeded per item.
  const [name, setName] = useState('');
  const [event, setEvent] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [subject, setSubject] = useState('');
  const [codename, setCodename] = useState('');
  const [definition, setDefinition] = useState('');
  const [categoryCode, setCategoryCode] = useState('');
  const [attributes, setAttributes] = useState<AttributeRow[]>([]);
  const [tokensJson, setTokensJson] = useState('');
  const [emailShell, setEmailShell] = useState('');
  const [rawJson, setRawJson] = useState('');

  useEffect(() => {
    if (!item) return;
    const p = item.payload;
    setName(String(p.name ?? ''));
    setEvent(String(p.event ?? ''));
    setEnabled(Boolean(p.enabled));
    setSubject(String(p.email_subject ?? ''));
    setCodename(String(p.codename ?? ''));
    setDefinition(String(p.definition ?? ''));
    setCategoryCode(String(p.category_code ?? ''));
    setAttributes(
      Array.isArray(p.attributes) ? (p.attributes as AttributeRow[]).map((a) => ({ ...a })) : [],
    );
    setTokensJson(JSON.stringify(p.tokens ?? {}, null, 2));
    setEmailShell(String(p.email_shell ?? ''));
    setRawJson(JSON.stringify(p, null, 2));
  }, [item]);

  const eventsQuery = useQuery({
    queryKey: ['notification-events'],
    queryFn: () => listEvents(),
    staleTime: 5 * 60_000,
    enabled: item?.kind === 'notification',
  });
  const eventOptions: NotificationEvent[] = eventsQuery.data?.events ?? [];

  if (!item) return null;
  const kind = item.kind;
  const hasForm = FORM_KINDS.has(kind);

  const buildPayload = (): Record<string, unknown> | null => {
    const base = { ...item.payload };
    switch (kind) {
      case 'notification':
        return { ...base, name: name.trim(), event: event.trim(), enabled };
      case 'delivery_method':
        return { ...base, email_subject: subject, enabled };
      case 'block':
        return { ...base, codename: codename.trim(), name: name.trim() || codename.trim(), definition };
      case 'tag_category': {
        const cleaned = attributes
          .map((a) => ({ ...a, attribute: String(a.attribute ?? '').trim() }))
          .filter((a) => a.attribute);
        return { ...base, category_code: categoryCode.trim(), attributes: cleaned };
      }
      case 'theme': {
        let tokens: unknown;
        try {
          tokens = JSON.parse(tokensJson || '{}');
        } catch (e) {
          snackbar.show(`Tokens are not valid JSON: ${(e as Error).message}`, 'error');
          return null;
        }
        return { ...base, codename: codename.trim(), name: name.trim(), tokens, email_shell: emailShell };
      }
      default:
        try {
          return JSON.parse(rawJson) as Record<string, unknown>;
        } catch (e) {
          snackbar.show(`Not valid JSON: ${(e as Error).message}`, 'error');
          return null;
        }
    }
  };

  const save = async () => {
    const payload = buildPayload();
    if (payload === null) return;
    setSaving(true);
    try {
      await proposalsApi.updateItem(proposalId, item.id, { payload });
      snackbar.show('Item updated.', 'success');
      onSaved();
      onClose();
    } catch (e) {
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const patchAttribute = (index: number, value: string) =>
    setAttributes((prev) => prev.map((a, i) => (i === index ? { ...a, attribute: value } : a)));
  const removeAttribute = (index: number) => setAttributes((prev) => prev.filter((_, i) => i !== index));

  return (
    <AppDialog
      open
      onClose={onClose}
      size={kind === 'block' || kind === 'theme' || !hasForm ? 'md' : 'sm'}
      title={`Edit ${kind.replace('_', ' ')}`}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={() => void save()} disabled={saving}>
            Save
          </Button>
        </>
      }
    >
      {/* pt keeps the first row's floating labels clear of the dialog title
          (outlined labels shrink upward and get clipped without it). */}
      <Box sx={{ pt: 1 }}>
      {kind === 'notification' && (
        <>
          <TextField
            fullWidth
            size="small"
            label="Notification name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="How the notification appears in the Notifications grid after apply."
            sx={{ mb: 2 }}
          />
          <Autocomplete<NotificationEvent>
            size="small"
            options={eventOptions}
            loading={eventsQuery.isLoading}
            value={eventOptions.find((o) => o.code === event) ?? null}
            onChange={(_, v) => setEvent(v?.code ?? '')}
            getOptionLabel={(o) => o.code}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Event"
                helperText="When this notification fires. Changing it changes which template variables are valid."
              />
            )}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
            label="Enabled on apply"
          />
          {enabled && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Imported notifications normally arrive disabled — enabling here means it starts sending as
              soon as the proposal is applied.
            </Alert>
          )}
        </>
      )}

      {kind === 'delivery_method' && (
        <>
          <TextField
            fullWidth
            size="small"
            label="Email subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            helperText="Django template syntax is allowed — e.g. {{ participant.tour.name }}."
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
            label="Delivery method enabled"
          />
        </>
      )}

      {kind === 'block' && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
            <TextField
              size="small"
              label="Codename"
              value={codename}
              onChange={(e) => setCodename(e.target.value)}
              helperText="Lowercase letters, digits, underscores — how templates reference the block."
            />
            <TextField
              size="small"
              label="Display name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              helperText="Shown in the Blocks catalog."
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Definition (Django template)
          </Typography>
          <TemplateCodeEditor
            value={definition}
            onChange={setDefinition}
            language="django"
            baseLanguage="html"
            height={320}
            aria-label="Block definition"
          />
        </>
      )}

      {kind === 'tag_category' && (
        <>
          <TextField
            fullWidth
            size="small"
            label="Category code"
            value={categoryCode}
            onChange={(e) => setCategoryCode(e.target.value)}
            helperText="Lowercase identifier for the tag category (e.g. booking_summary)."
            sx={{ mb: 2 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Attributes — the facts templates will read from this category. Remove anything the design
            doesn&apos;t truly need; every attribute must eventually be populated by an integration
            mapping or manual tags.
          </Typography>
          {attributes.map((attr, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                fullWidth
                value={String(attr.attribute ?? '')}
                onChange={(e) => patchAttribute(index, e.target.value)}
              />
              <IconButton size="small" onClick={() => removeAttribute(index)} aria-label="Remove attribute">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAttributes((prev) => [...prev, { attribute: '' }])}
          >
            Add attribute
          </Button>
        </>
      )}

      {kind === 'theme' && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
            <TextField
              size="small"
              label="Codename"
              value={codename}
              onChange={(e) => setCodename(e.target.value)}
              helperText="Identifier referenced by operator config."
            />
            <TextField
              size="small"
              label="Display name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Box>
          <Accordion variant="outlined" disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">Advanced: tokens & email shell</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                fullWidth
                multiline
                minRows={6}
                size="small"
                label="Design tokens (JSON)"
                value={tokensJson}
                onChange={(e) => setTokensJson(e.target.value)}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                helperText="Exposed to templates as theme.* — e.g. theme.color.primary."
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Email shell (wraps every rendered body; must keep the {'{{ body }}'} slot)
              </Typography>
              <TemplateCodeEditor
                value={emailShell}
                onChange={setEmailShell}
                language="django"
                baseLanguage="html"
                height={240}
                aria-label="Theme email shell"
              />
            </AccordionDetails>
          </Accordion>
        </>
      )}

      {!hasForm && (
        <>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            No structured form exists for “{kind}” yet — this edits the raw payload.
          </Alert>
          <TextField
            fullWidth
            multiline
            minRows={12}
            maxRows={24}
            value={rawJson}
            onChange={(e) => setRawJson(e.target.value)}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
          />
        </>
      )}
      </Box>
    </AppDialog>
  );
}
